import {
  PerformanceEvaluationAnalyticsFragment,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceTargetWithPublishedEvaluationsFragment,
  QuestionnaireContentFragment,
  QuestionnaireQuestionAnsweredFragment,
  QuestionnairesQuestionnaireQuestionAnswerTypesEnum,
} from 'generated/resources/graphql'

/**
 * Groups elements of an array by a specified key.
 *
 * @template A The type of elements in the array.
 * @template K The type of key used for grouping.
 * @param {A[]} xs The array of elements to group.
 * @param {(a: A) => K} key The function to extract the grouping key from each element.
 * @returns {Map<K, A[]>} A Map where keys are the grouping key and values are arrays of elements with that key.
 */
function groupBy<A, K>(xs: A[], key: (a: A) => K): Map<K, A[]> {
  return xs.reduce((m, x) => {
    const k = key(x)
    const elements_of_k = m.get(k) || []
    elements_of_k.push(x)
    m.set(k, elements_of_k)
    return m
  }, new Map<K, A[]>())
}

export const extractQuestions = (
  questionnaires: PerformanceQuestionnaireByReviewerStrategyFragment,
  strategy: PerformanceReviewerStrategiesStrategyEnum
): QuestionnaireContentFragment['questions'] => {
  const questionnaireByStrategy = () => {
    switch (strategy) {
      case 'self':
        return [
          ...(questionnaires.employeeScoreSelfQuestionnaire?.content ?? []),
          ...(questionnaires.selfQuestionnaire?.content ?? []),
        ]
      case 'manager':
        return [
          ...(questionnaires.employeeScoreManagerQuestionnaire?.content ?? []),
          ...(questionnaires.managerQuestionnaire?.content ?? []),
        ]
      case 'direct_reports':
        return questionnaires.directReportQuestionnaire?.content ?? []
      case 'peers':
        return questionnaires.peersQuestionnaire?.content ?? []
      default:
        return []
    }
  }

  return questionnaireByStrategy().flatMap((item) => item.questions)
}

type AnswersWithTarget = {
  answers: PerformanceEvaluationAnalyticsFragment[]
  targetAccess: { id: number; fullName: string; avatar?: string | null }
}[]

export const publishedAnswersWithTargetByReviewType = (
  targetsWithPublishedEvaluations: PerformanceTargetWithPublishedEvaluationsFragment[],
  reviewType: PerformanceReviewerStrategiesStrategyEnum
): AnswersWithTarget => {
  return targetsWithPublishedEvaluations
    .map(({ access, evaluations }) => {
      if (!access) return

      return {
        targetAccess: {
          id: access.id,
          fullName: access.fullName,
          avatar: access.avatar,
        },
        answers: evaluations.filter((ev) => ev?.evaluationType === reviewType),
      }
    })
    .filter(Boolean)
}

export type Reviewer = {
  id: number
  avatar?: string
  name: string
}

export type Target = {
  id: number
  avatar?: string
  name: string
}

export type Answer = {
  questionId: string
  targetId: number
  reviewerId: number
  score?: number
  scoreLabel?: string
  answerText?: string
  answerChoice?: string[]
  answerInt?: number
  type: 'low' | 'normal' | 'high' | null
  comment?: string | null
}

type Presented = {
  answers: Answer[]
  targets: Target[]
  targetsWithReviewers: Record<number, Reviewer[]>
}

type NumericAnswer = {
  strategy: PerformanceReviewerStrategiesStrategyEnum
  questionId: string
  type: QuestionnairesQuestionnaireQuestionAnswerTypesEnum
  score: number
  scoreLabel?: string
  scale?: number[] | null | undefined
  comment?: string | null | undefined
  reviewerId: number
}

type IntegerAnswer = {
  strategy: PerformanceReviewerStrategiesStrategyEnum
  questionId: string
  type: QuestionnairesQuestionnaireQuestionAnswerTypesEnum
  answerInt?: number | undefined
  reviewerId: number
}

type TextAnswer = {
  strategy: PerformanceReviewerStrategiesStrategyEnum
  questionId: string
  type: QuestionnairesQuestionnaireQuestionAnswerTypesEnum
  answerText?: string
  answerChoice?: string[]
  reviewerId: number
}

type AnswerTypes = NumericAnswer | IntegerAnswer | TextAnswer

type ScoreType = 'low' | 'normal' | 'high'

function scoreType(score: number, scale?: number[] | null): ScoreType {
  let scoreType: ScoreType = 'normal'

  if (scale) {
    const min = Math.min(...scale)
    const max = Math.max(...scale)

    const threshold = Math.floor((max - min) / 3)

    if (max - threshold <= score) {
      scoreType = 'high'
    } else if (min + threshold >= score) {
      scoreType = 'low'
    }
  }
  return scoreType
}

function extractNumericalAnswer(a: QuestionnaireQuestionAnsweredFragment): {
  score: number
  scoreType: ScoreType
  scoreLabel?: string
  comment?: string | null
  scale?: number[] | null
} | null {
  let answer,
    scale,
    comment = null
  if (a.question.answerType == 'rating' && a.answer?.answerRating?.value) {
    answer = a.answer.answerRating.value
    scale = a.question.scale?.map((s) => s.value)
    comment = a.answer.answerRating.comment
  } else {
    return null
  }

  return {
    score: answer,
    scoreType: scoreType(answer, scale),
    scoreLabel: a.question.scale?.find((s) => s.value === answer)?.text,
    comment: comment,
    scale: scale,
  }
}

function formatAnswer(
  reviewerId: number,
  evaluationType: PerformanceReviewerStrategiesStrategyEnum,
  answeredQuestion: QuestionnaireQuestionAnsweredFragment
): AnswerTypes | null {
  const { question, answer } = answeredQuestion
  if (!answer) {
    return null
  }
  if (question.answerType == 'rating') {
    const extracted = extractNumericalAnswer(answeredQuestion)
    if (!extracted) {
      return null
    }

    return {
      reviewerId,
      strategy: evaluationType,
      questionId: question.uuid,
      type: question.answerType,
      ...extracted,
    }
  } else if (question.answerType == 'number') {
    const numericValue = answer.answerFloat || answer.answerInt || 0

    return {
      reviewerId,
      strategy: evaluationType,
      questionId: question.uuid,
      type: question.answerType,
      answerInt: numericValue,
    }
  } else {
    return {
      reviewerId,
      strategy: evaluationType,
      questionId: question.uuid,
      type: question.answerType,
      answerText: answer.answerText ?? '',
      answerChoice: answer.answerChoice ?? [],
    }
  }
}

// Exctract answers for type of review and target
export function present(
  answersWithTarget: {
    answers: PerformanceEvaluationAnalyticsFragment[]
    targetAccess: { id: number; fullName: string; avatar?: string | null }
  }[],
  reviewType: PerformanceReviewerStrategiesStrategyEnum,
  onlyQuantitative = false
): Presented {
  const qualitativeAnswers: Answer[] = []
  const quantitativeAnswers: Answer[] = []
  const targets: Target[] = []
  const targetsWithReviewers: Record<string, Target[]> = {}

  groupBy(answersWithTarget, (o) => o.targetAccess.id).forEach((targetAndAnswers, targetId) => {
    targetAndAnswers.forEach(({ answers: evaluations, targetAccess }) => {
      const answers = evaluations
        .flatMap((evaluation) => {
          const evaluationType = evaluation?.evaluationType
          const reviewerId = evaluation?.reviewerAccess?.id
          if (!evaluationType || !reviewerId) return null

          const answeredQuestionnaire = [
            ...(evaluation?.answeredQuestionnaires?.answeredEmployeeScoreQuestionnaire?.content ??
              []),
            ...(evaluation?.answeredQuestionnaires?.answeredQuestionnaireWithSections?.content ??
              []),
          ]

          return answeredQuestionnaire
            .flatMap(({ questions }) => questions)
            .map((x) => formatAnswer(reviewerId, evaluationType, x))
        })
        .filter((x): x is AnswerTypes => x !== null && x.strategy === reviewType)

      const integerAnswer = answers.filter((x): x is IntegerAnswer => 'answerInt' in x)
      const numericAnswers = answers.filter((x): x is NumericAnswer => 'score' in x)
      const textAnswers = answers.filter(
        (x): x is TextAnswer => 'answerText' in x || 'answerChoice' in x
      )

      const reviewersNumericAnswer = new Set<number>()
      const targetNumericAnswers = numericAnswers.map((answerNumeric) => {
        const aggregatedScore = answerNumeric.score ?? 0.0
        const scale = answerNumeric.scale

        const aggregatedScoreType = scoreType(aggregatedScore, scale)
        const comment = answerNumeric.comment
        reviewersNumericAnswer.add(answerNumeric.reviewerId)
        return {
          reviewerId: answerNumeric.reviewerId,
          questionId: answerNumeric.questionId,
          targetId: targetId,
          score: aggregatedScore,
          scoreLabel: answerNumeric.scoreLabel,
          comment: comment,
          type: aggregatedScoreType,
        }
      })
      quantitativeAnswers.push(...targetNumericAnswers)

      const reviewersQualitativeAnswers = new Set<number>()
      const targetQualitativeAnswers = onlyQuantitative
        ? []
        : [...textAnswers, ...integerAnswer]
            .map((answerQualitative) => {
              reviewersQualitativeAnswers.add(answerQualitative.reviewerId)
              if ('answerText' in answerQualitative) {
                return {
                  reviewerId: answerQualitative.reviewerId,
                  questionId: answerQualitative.questionId,
                  targetId: targetId,
                  answerText: answerQualitative.answerText,
                  answerChoice: answerQualitative.answerChoice,
                  type: null,
                }
              } else if ('answerInt' in answerQualitative) {
                return {
                  reviewerId: answerQualitative.reviewerId,
                  questionId: answerQualitative.questionId,
                  targetId: targetId,
                  answerInt: answerQualitative.answerInt,
                  type: null,
                }
              }
            })
            .filter(Boolean)

      if (!onlyQuantitative) {
        qualitativeAnswers.push(...targetQualitativeAnswers)
      }

      const targetAnswers = [...targetNumericAnswers, ...targetQualitativeAnswers]

      if (targetAnswers.length) {
        targets.push({
          id: targetAccess.id,
          avatar: targetAccess.avatar || undefined,
          name: targetAccess.fullName ?? '',
        })

        if (!targetsWithReviewers[targetAccess.id]) {
          targetsWithReviewers[targetAccess.id] = []
        }

        evaluations.forEach((evaluation) => {
          const reviewer = evaluation?.reviewerAccess
          if (!reviewer) {
            return
          }
          const isReviewerNumeric = reviewersNumericAnswer.has(reviewer.id)
          const isReviewerQualitative = reviewersQualitativeAnswers.has(reviewer.id)

          const isReviewer = onlyQuantitative
            ? isReviewerNumeric
            : isReviewerNumeric || isReviewerQualitative
          if (!isReviewer) {
            return
          }

          targetsWithReviewers[targetAccess.id]?.push({
            id: reviewer.id,
            avatar: reviewer.avatar || undefined,
            name: reviewer.fullName,
          })
        })
      }
    })
  })

  return {
    answers: [...(onlyQuantitative ? [] : qualitativeAnswers), ...quantitativeAnswers],
    targets,
    targetsWithReviewers,
  }
}
